import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import {
  Box,
  Divider,
  FormField,
  Heading,
  Icon,
  Input,
  Label,
  List,
  Modal,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconUserAddFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserProfileWebApiHandler_setName,
  useRentalManagerUserPropertySummaryWebApiHandler_applications,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import PropertiesActions from '../../../actions/PropertiesActions';
import UserManagementActions from '../../../actions/UserManagementActions';
import { RMWContext } from '../../../providers/RMWContext';
import { PropertiesSelectors, RoutingSelectors, UserSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';
import propertyUtils from '../../../utils/propertyUtils';

import ErrorMessageConstants from '../../../constants/ErrorMessageConstants';

import ApplicationsTermsOfUse from '../../../components/ApplicationsTermsOfUse';
import IntermediateStep from '../../../components/IntermediateStep';
import ModalFooter from '../../../components/Modal/ModalFooter';
import SpinnerContainer from '../../../components/SpinnerContainer';

import type { ChangeEvent } from 'react';

const STEPS = {
  loading: 'loading',
  initialOneAdmin: 'initialOneAdmin',
  initialMultipleAdmins: 'initialMultipleAdmins',
  name: 'name',
  accessUnavailable: 'accessUnavailable',
  verifyIdentity: 'verifyIdentity',
  success: 'success',
  loadingError: 'loadingError',
} as const;

const FOOTER_CONFIG = {
  [STEPS.initialOneAdmin]: {
    submitText: 'OK',
    cancelText: 'Cancel',
  },
  [STEPS.initialMultipleAdmins]: {
    submitText: 'Yes',
    cancelText: 'Not right now',
  },
  [STEPS.name]: {
    submitText: 'Next',
    cancelText: 'Cancel',
  },
  [STEPS.success]: {},
  [STEPS.accessUnavailable]: {
    submitText: 'OK',
  },
  [STEPS.verifyIdentity]: {
    submitText: 'Verify your identity',
    cancelText: 'Cancel',
  },
  [STEPS.loadingError]: {},
} as const;

type Props = {
  setShowApplicationsPermissionSetupModal: (arg: boolean) => void;
  verifyIdentitySuccess?: boolean;
};

const ApplicationsPermissionSetupModal = ({
  setShowApplicationsPermissionSetupModal,
  verifyIdentitySuccess = false,
}: Props) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { paths } = useContext(RMWContext);

  const currentProperty = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));
  const currentAddress = propertyUtils.formatAddress(currentProperty);
  const user = useSelector((state) => UserSelectors.makeUser(state));
  const pathname = useSelector((state) => RoutingSelectors.makePathname(state));

  const [step, setStep] = useState<keyof typeof STEPS>(STEPS.loading);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adminCount, setAdminCount] = useState(0);

  const { mutate: setUserName } = useRentalManagerUserProfileWebApiHandler_setName({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'useRentalManagerUserProfileWebApiHandler_setName',
      },
      invalidates: ['users-profile-setName'],
    },
  });

  const [name, setName] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
  });
  const hasName = name.firstName && name.lastName;

  useEffect(() => {
    if (verifyIdentitySuccess) {
      handleEnableApplications();
    } else {
      dispatch(UserManagementActions.getUserManagementProfiles())
        // @ts-expect-error
        .then((result: { payload: { users?: Array<{ isAdmin: boolean; userToken: string }> } }) => {
          const companyAdmins = (result.payload?.users || []).filter((newUser) => newUser.isAdmin);
          const newAdminCount = companyAdmins.length;
          const isUserCompanyAdmin = companyAdmins.find((admin) => admin.userToken === user.userToken);

          if (isUserCompanyAdmin && newAdminCount > 0) {
            setStep(newAdminCount === 1 ? STEPS.initialOneAdmin : STEPS.initialMultipleAdmins);
            setAdminCount(companyAdmins.length);
          } else {
            setStep(STEPS.loadingError);
          }
        });
    }
  }, []);

  const handleNameChange = (value: string, key: string) => {
    setName({
      ...name,
      [key]: value,
    });
    if (error) {
      setError('');
    }
  };

  const handleEnableApplications = () => {
    dispatch(PropertiesActions.isApplicationsAccepted(currentProperty.listingAlias, true, true))
      // @ts-expect-error
      .then(() => setStep(STEPS.success))
      .catch(() => {
        setError('There was an error enabling applications. Please try again.');
        if (verifyIdentitySuccess) {
          return setStep(STEPS.loadingError);
        }
      })
      .finally(() => {
        setIsSubmitting(false);
        queryClient.invalidateQueries({
          queryKey: useRentalManagerUserPropertySummaryWebApiHandler_applications.getKey({
            listingAlias: currentProperty.listingAlias,
          }),
        });
      });
  };

  const handleCancel = () => {
    if (step === STEPS.initialOneAdmin || step === STEPS.verifyIdentity || step === STEPS.name) {
      setShowApplicationsPermissionSetupModal(false);
    } else if (step === STEPS.initialMultipleAdmins) {
      setStep(STEPS.accessUnavailable);
    }
  };

  const handleSubmit = () => {
    if (step === STEPS.accessUnavailable) {
      setShowApplicationsPermissionSetupModal(false);
    } else if (step === STEPS.initialOneAdmin || step === STEPS.initialMultipleAdmins) {
      if (hasName && user.isIdVerified) {
        setIsSubmitting(true);
        handleEnableApplications();
      } else if (!hasName) {
        setStep(STEPS.name);
      } else if (!user.isIdVerified) {
        setStep(STEPS.verifyIdentity);
      }
    } else if (step === STEPS.name) {
      setIsSubmitting(true);
      setUserName(
        {
          getParams: { firstName: name.firstName, lastName: name.lastName },
        },
        {
          onSuccess: () => {
            if (user.isIdVerified) {
              handleEnableApplications();
            } else {
              setStep(STEPS.verifyIdentity);
            }
          },
          onError: () => setError('There was an error adding your name. Please try again.'),
          onSettled: () => setIsSubmitting(false),
        },
      );
    } else if (step === STEPS.verifyIdentity) {
      const applicationsUrlPath = `${pathname}${window.location.search}`;
      const search = new URLSearchParams({ applicationsUrlPath }).toString();
      window.location.href = `/rental-manager/applications/id-verification?${search}`;
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && setShowApplicationsPermissionSetupModal(false)}
      header={<Heading level={2}>Permission setup</Heading>}
      body={
        <div data-testid="ApplicationsPermissionSetupModal">
          {step === STEPS.loading && <SpinnerContainer />}
          {step === STEPS.loadingError && <Text>{error || ErrorMessageConstants.DEFAULT_ERROR}</Text>}
          {step === STEPS.initialOneAdmin && (
            <>
              <Box css={{ marginBottom: '200' }}>
                <Heading level={4}>You’re the application manager</Heading>
              </Box>
              <Text textStyle="body-bold" asChild css={{ marginBottom: '400' }}>
                <p>You’ll be able to give people permission to access applications.</p>
              </Text>
              <Text asChild>
                <p>As the application manager, you will:</p>
              </Text>
              <List.Root>
                <List.Item css={{ paddingBlock: '0' }}>Receive permission requests from teammates</List.Item>
                <List.Item css={{ paddingBlock: '0' }}>
                  Manage application access permissions for up to 5 users on the account
                </List.Item>
                <List.Item css={{ paddingBlock: '0' }}>Be listed as the person viewing applications</List.Item>
              </List.Root>
              <Text asChild css={{ paddingTop: '400', paddingBottom: '200' }}>
                <p>
                  When you give a user applications permission, they’ll be able to review applications for their
                  assigned properties.
                </p>
              </Text>
              <ApplicationsTermsOfUse />
            </>
          )}
          {step === STEPS.initialMultipleAdmins && (
            <>
              <Box css={{ marginBottom: '200' }}>
                <Heading level={4}>Become application manager?</Heading>
              </Box>
              <Paragraph css={{ marginBottom: '400' }}>
                Your account now allows up to 5 people to access applications. The application manager is the only
                person on the account who can assign those permissions.
              </Paragraph>
              <Text textStyle="body-bold" asChild css={{ marginBottom: '200' }}>
                <p>Your account doesn’t currently have an application manager assigned.</p>
              </Text>
              <Text asChild>
                <p>The application manager will:</p>
              </Text>
              <List.Root>
                <List.Item css={{ paddingBlock: '0' }}>Receive permission requests from teammates</List.Item>
                <List.Item css={{ paddingBlock: '0' }}>
                  Manage application access permissions for up to 5 users on the account
                </List.Item>
                <List.Item css={{ paddingBlock: '0' }}>Be listed as the person viewing applications</List.Item>
              </List.Root>
              <Text textStyle="body-bold" asChild css={{ paddingTop: '400' }}>
                <p>Would you like the application manager role assigned to you?</p>
              </Text>
              <ApplicationsTermsOfUse />
            </>
          )}
          {step === STEPS.name && (
            <>
              <Box css={{ marginBottom: '200' }}>
                <Heading level={4}>Contact name</Heading>
              </Box>
              <Paragraph css={{ marginBottom: '1000' }}>
                A contact must be displayed with the listing because renters are legally entitled to see who has access
                to their application.
              </Paragraph>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box css={{ flex: 1 }}>
                  <FormField
                    control={
                      <Input
                        className="fs-mask"
                        value={name.firstName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleNameChange(e.target.value, 'firstName')}
                      />
                    }
                    label={<Label>First name</Label>}
                    css={{ marginRight: '200' }}
                  />
                </Box>
                <Box css={{ flex: 1 }}>
                  <FormField
                    control={
                      <Input
                        className="fs-mask"
                        value={name.lastName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleNameChange(e.target.value, 'lastName')}
                      />
                    }
                    label={<Label>Last name</Label>}
                    css={{ marginLeft: '200' }}
                  />
                </Box>
              </Box>
            </>
          )}
          {step === STEPS.verifyIdentity && (
            <>
              <Box css={{ marginBottom: '200' }}>
                <Heading level={4}>Verify your identity now</Heading>
              </Box>
              <Paragraph css={{ marginBottom: '400' }}>
                To protect applicants’ confidential information, we’ll need to verify your identity before you can view
                applications.
              </Paragraph>
              <Paragraph>Applications include each renter’s:</Paragraph>
              <List.Root css={{ marginBottom: '400', fontSize: 'body-xs' }}>
                <List.Item css={{ paddingBlock: 0 }}>Comprehensive credit report</List.Item>
                <List.Item css={{ paddingBlock: 0 }}>Background check</List.Item>
                <List.Item css={{ paddingBlock: 0 }}>Income documents (if provided)</List.Item>
              </List.Root>
              <Paragraph>
                Once completed, your ID verification will be valid for <strong>335 days.</strong>
              </Paragraph>
            </>
          )}
          {step === STEPS.success && (
            <>
              <IntermediateStep headingLevel={4} headingText="Permissions setup complete">
                <Paragraph css={{ marginBottom: '400' }}>
                  You’ll receive all applications for the property at
                  {` ${currentAddress.formattedAddress}.`}
                </Paragraph>
                <Paragraph css={{ marginBottom: '400' }}>
                  Your name will be displayed as the person viewing applications.
                </Paragraph>
              </IntermediateStep>
              {adminCount > 1 && (
                <>
                  <Divider css={{ marginBlock: '400' }} />
                  <Box
                    css={{
                      display: 'flex',
                      borderRadius: '100',
                      backgroundColor: 'bg.accent.blue.soft',
                      padding: '600',
                    }}
                  >
                    <Icon render={<IconUserAddFilled />} size="xl" />
                    <Box css={{ paddingLeft: '400' }}>
                      <Text textStyle="body-bold" asChild>
                        <p>You’re the application manager</p>
                      </Text>
                      <Text asChild>
                        <p>You can allow up to 5 additional users to access applications received by your account.</p>
                      </Text>
                      <TextButton asChild>
                        <Link to={`${paths.userManagementPage}/${paths.userManagementApplicationPermissions}`}>
                          Manage user permissions
                        </Link>
                      </TextButton>
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}
          {step === STEPS.accessUnavailable && (
            <>
              <Box css={{ marginBottom: '200' }}>
                <Heading level={4}>Access unavailable</Heading>
              </Box>
              <Paragraph css={{ marginBottom: '400' }}>
                Your account isn’t set up with the user permissions necessary for you to enable applications.
              </Paragraph>
              <Paragraph css={{ marginBottom: '400' }}>
                First, someone on your account needs to become the application manager. Then, they can assign you
                permission to enable and access applications.
              </Paragraph>
            </>
          )}
        </div>
      }
      footer={
        step !== STEPS.loading &&
        step !== STEPS.success &&
        step !== STEPS.loadingError && (
          <ModalFooter
            onCancel={step !== STEPS.accessUnavailable ? handleCancel : null}
            onSubmit={handleSubmit}
            cancelText={(FOOTER_CONFIG[step] as Record<string, string>).cancelText}
            submitText={FOOTER_CONFIG[step].submitText}
            cancelType="button"
            isSubmitting={isSubmitting}
            isSubmitDisabled={step === STEPS.name && (!name.firstName || !name.lastName)}
            hasError={!!error}
            errorMessage={error}
          />
        )
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default ApplicationsPermissionSetupModal;
